import { FC, useMemo } from 'react';
import JSZip from 'jszip';
import { useTranslation } from 'react-i18next';

import cancelSvg from '/prospectCancel.svg';
import downloadSvg from '/downloadGray.svg';

import Loading from 'components/Loading';

interface Props {
  setModal: (value: boolean) => void;
  files: JSZip.JSZipObject[];
  isFilesLoading: boolean;
  selectedProduct: string;
}

const DownloadModal: FC<Props> = ({
  setModal,
  files,
  isFilesLoading,
  selectedProduct,
}) => {
  const { t } = useTranslation();

  const productFiles = useMemo(
    () => {
      const selectedProductBrand = selectedProduct?.toLowerCase().split('-')[0];

      if (!selectedProductBrand) {
        return [];
      }

      return files
        .filter((file) => file
          .name
          .toLowerCase()
          .includes(selectedProductBrand.trim()));
    },
    [selectedProduct, files]);

  const reactNativeWebView = (window as any)?.ReactNativeWebView;

  async function encodeFileToBase64(file: JSZip.JSZipObject): Promise<string> {
    const content = await file.async('arraybuffer');
    const blob = new Blob([content], { type: 'application/octet-stream' });

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
    }).then((dataUrl) => dataUrl.split(',')[1]);
  }

  const handleOnDownload = (name: string) => async () => {
    const productFile = files.find((file) => file.name === name);

    if (productFile) {
      if (reactNativeWebView) {
        const base64String = await encodeFileToBase64(productFile);
        const fileName = productFile.name;

        reactNativeWebView.postMessage(
          JSON.stringify({
            type: 'downloadFile',
            fileName,
            base64String,
          }),
        );
      } else {
        productFile.async('blob').then((data) => {
          const fileurl = window.URL.createObjectURL(data);
          const link = document.createElement('a');
          link.href = fileurl;
          link.setAttribute('download', name);
          document.body.appendChild(link);
          link.click();
        });
      }
    }
  };

  return (
    <div className="fixed z-50 top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.5)] flex items-center justify-center h-screen w-screen">
      <div className="bg-white p-8 md:p-4 rounded-2xl flex w-[500px] flex-col items-center max-w-125 md:max-w-80 text-center">
        <div
          onClick={() => setModal(false)}
          className="flex justify-end items-end w-full cursor-pointer"
        >
          <img src={cancelSvg} alt="cancel" />
        </div>
        <div className="text-2xl font-bold text-primary mb-3 md:text-xl text-center">
          <div className="text-black">{t('donwloadPdf')}</div>
        </div>
        {isFilesLoading ? <Loading size={25} color="#007AC1" /> : (
          <>
            <div className="flex mb-6">
              <div className="text-secondaryText">Choose the language</div>
            </div>
            {productFiles?.map((file) => (
              <div className="flex justify-between mb-4 w-full" key={file.name}>
                <div className="text-black text-left">{file.name}</div>
                <div className="text-black font-medium">
                  <div className="cursor-pointer md:w-[40px] mr-3" onClick={handleOnDownload(file.name)}>
                    <img src={downloadSvg} alt="edit" className="stroke-current text-secondaryText" />
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default DownloadModal;
